import { sourceHttpClient } from '../core/SourceHttp.ts';
import { yomiriDatabase } from '../storage/YomiriDatabase.ts';

export interface ReleaseMedia {
  id: number;
  siteUrl: string;
  status: string;
  averageScore?: number | null;
  title: { romaji?: string; english?: string; native?: string };
  synonyms: string[];
  nextAiringEpisode?: { episode: number; airingAt: number } | null;
}
interface CachedSchedule { until: number; media: ReleaseMedia[] }
const pending = new Map<string, Promise<ReleaseMedia[]>>();
let cooldownUntil = 0;
export const normalizeTitle = (title: string) => title.normalize('NFKD').replace(/\p{M}/gu, '').toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');
export const scheduleKey = (title: string, type: 'ANIME' | 'MANGA') => `release-schedule:${type}:${normalizeTitle(title)}`;

export async function releaseCandidates(title: string, type: 'ANIME' | 'MANGA', cachedOnly = false): Promise<ReleaseMedia[]> {
  const key = scheduleKey(title, type);
  const cached = await yomiriDatabase.getSetting<CachedSchedule>(key);
  if (cached && (cachedOnly || (cached.until > Date.now() && cached.media.every(item => 'averageScore' in item)))) return cached.media;
  if (cachedOnly) return [];
  if (Date.now() < cooldownUntil) throw new Error('El calendario está temporalmente limitado.');
  const existing = pending.get(key);
  if (existing) return existing;
  const promise = (async () => {
    const response = await sourceHttpClient.text({ sourceId: 'anilist', url: 'https://graphql.anilist.co',
      method: 'POST', responseType: 'text', credentials: 'omit', corsOpen: true,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ query: `query($search:String!,$type:MediaType!){Page(perPage:5){media(search:$search,type:$type){id siteUrl status averageScore title{romaji english native} synonyms nextAiringEpisode{episode airingAt}}}}`, variables: { search: title, type } }),
      cache: { ttlMs: 0, scope: 'public' },
    });
    if (response.status === 429) { cooldownUntil = Date.now() + 60000; throw new Error('El calendario está temporalmente limitado.'); }
    if (response.status >= 400) throw new Error('No se pudo consultar el calendario.');
    const payload = JSON.parse(response.body) as { data?: { Page?: { media?: ReleaseMedia[] } }; errors?: unknown[] };
    if (payload.errors || !Array.isArray(payload.data?.Page?.media)) throw new Error('No se pudo consultar el calendario.');
    const media = payload.data.Page.media;
    const next = Math.min(...media.flatMap(item => item.nextAiringEpisode && item.nextAiringEpisode.airingAt * 1000 > Date.now()
      ? [item.nextAiringEpisode.airingAt * 1000 + 60000] : []));
    await yomiriDatabase.setSetting(key, { until: Math.min(Date.now() + 3600000, next), media });
    return media;
  })().finally(() => pending.delete(key));
  pending.set(key, promise);
  return promise;
}
